import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Search, FileText, ChevronRight, CheckCircle, XCircle, Users } from "lucide-react";
import { useTheme } from "../context/ThemeContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_COLORS = {
  Climate: "#10B981", Housing: "#3B82F6", Economy: "#F59E0B",
  Indigenous: "#DC2626", Disability: "#8B5CF6", Environment: "#059669",
  Integrity: "#6366F1", Defence: "#1E40AF", Education: "#0EA5E9",
  "Social Policy": "#EC4899", "Gambling Reform": "#D97706",
  "Foreign Policy": "#64748B", Energy: "#EF4444", Immigration: "#7C3AED",
  Agriculture: "#065F46", "Digital Safety": "#0891B2", "Human Rights": "#BE185D",
};

export default function PoliciesPage() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");

  const bg = isDark ? "#0F172A" : "#F9F7F2";
  const surface = isDark ? "#1E293B" : "#FFFFFF";
  const text = isDark ? "#F8FAFC" : "#0F172A";
  const textSec = isDark ? "#94A3B8" : "#475569";

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (category !== "all") params.append("category", category);
    if (search) params.append("search", search);
    axios.get(`${API}/policies?${params}`)
      .then((r) => setPolicies(r.data))
      .catch(() => setPolicies([]))
      .finally(() => setLoading(false));
  }, [category, search]);

  const allCategories = ["all", ...Object.keys(CATEGORY_COLORS)];
  const topCategories = allCategories.slice(0, 10);

  return (
    <div className="min-h-screen" style={{ backgroundColor: bg }} data-testid="policies-page">
      {/* Hero */}
      <div className="py-10 px-4 border-b-2 border-black" style={{ backgroundColor: "#0F172A" }}>
        <div className="max-w-6xl mx-auto">
          <button
            onClick={() => navigate("/politicians")}
            className="flex items-center gap-1.5 text-sm font-bold mb-5 hover:opacity-75 transition-opacity"
            style={{ color: "#FFC82E" }}
            data-testid="back-politicians-btn"
          >
            <ArrowLeft className="w-4 h-4" /> All Politicians
          </button>
          <div className="flex items-center gap-3 mb-2">
            <div className="px-3 py-1 text-xs font-black border-2 border-black" style={{ backgroundColor: "#FFC82E", color: "#000" }}>
              KEY VOTES
            </div>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-white mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            Policy Divisions
          </h1>
          <p className="text-sm" style={{ color: "#94A3B8" }}>
            Browse key bills and see which politicians voted for or against each one.
          </p>

          {/* Stats */}
          <div className="flex flex-wrap gap-3 mt-5">
            {[
              { label: "Key Divisions", value: policies.length || "—" },
              { label: "Tracked Politicians", value: 20 },
              { label: "Policy Categories", value: Object.keys(CATEGORY_COLORS).length },
            ].map(({ label, value }) => (
              <div key={label} className="border-2 border-black px-4 py-2" style={{ backgroundColor: "rgba(255,200,46,0.1)" }}>
                <span className="text-xl font-black text-white">{value}</span>
                <span className="text-xs font-bold ml-2" style={{ color: "#94A3B8" }}>{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Search */}
        <div className="flex flex-col gap-3 mb-8">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: textSec }} />
            <input
              type="text"
              placeholder="Search bills and policies…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 border-2 border-black text-sm font-medium outline-none"
              style={{ backgroundColor: surface, color: text }}
              data-testid="policy-search-input"
            />
          </div>
          {/* Category filters */}
          <div className="flex flex-wrap gap-1.5">
            {topCategories.map((cat) => {
              const color = CATEGORY_COLORS[cat];
              return (
                <button
                  key={cat}
                  onClick={() => setCategory(cat)}
                  data-testid={`category-filter-${cat}`}
                  className="px-3 py-1.5 text-xs font-bold border-2 border-black transition-all capitalize"
                  style={{
                    backgroundColor: category === cat ? (cat === "all" ? "#FFC82E" : color) : surface,
                    color: category === cat ? (cat === "all" ? "#000" : "#fff") : text,
                    boxShadow: category === cat ? "2px 2px 0 #000" : "none",
                  }}
                >
                  {cat === "all" ? "All Topics" : cat}
                </button>
              );
            })}
          </div>
        </div>

        {/* Policy cards */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="border-2 border-black h-28 animate-pulse" style={{ backgroundColor: surface }} />
            ))}
          </div>
        ) : policies.length === 0 ? (
          <div className="border-2 border-black p-10 text-center" style={{ backgroundColor: surface }} data-testid="no-policies">
            <p className="font-black text-base" style={{ color: text }}>No policies found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="policies-grid">
            {policies.map((policy) => {
              const catColor = CATEGORY_COLORS[policy.category] || "#6B7280";
              const ayePct = policy.total_votes > 0 ? Math.round((policy.aye_count / policy.total_votes) * 100) : 0;
              return (
                <button
                  key={policy.id}
                  onClick={() => navigate(`/policies/${policy.id}`)}
                  data-testid={`policy-card-${policy.id}`}
                  className="text-left border-2 border-black p-0 overflow-hidden hover:-translate-y-1 transition-transform group"
                  style={{ backgroundColor: surface, boxShadow: "3px 3px 0 #000" }}
                >
                  <div className="h-1.5" style={{ backgroundColor: catColor }} />
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex-1">
                        <p className="font-black text-sm leading-tight" style={{ color: text, fontFamily: "Outfit, sans-serif" }}>
                          {policy.bill}
                        </p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className="text-xs" style={{ color: textSec }}>{policy.date}</span>
                          <span className="text-xs font-bold px-1.5 py-0.5 border" style={{ color: catColor, borderColor: catColor }}>
                            {policy.category}
                          </span>
                          <div className="flex items-center gap-1 text-xs font-bold" style={{ color: policy.passed ? "#10B981" : "#EF4444" }}>
                            {policy.passed ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                            {policy.passed ? "Passed" : "Not passed"}
                          </div>
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 flex-shrink-0 mt-0.5 group-hover:translate-x-0.5 transition-transform" style={{ color: textSec }} />
                    </div>

                    {/* Vote bar */}
                    <div className="mt-3 pt-3 border-t-2" style={{ borderColor: isDark ? "#334155" : "#E5E7EB" }}>
                      <div className="flex items-center gap-3 mb-1.5">
                        <div className="flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full bg-green-500" />
                          <span className="text-xs font-black text-green-500">{policy.aye_count} Aye</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full bg-red-500" />
                          <span className="text-xs font-black text-red-500">{policy.no_count} No</span>
                        </div>
                        <div className="flex items-center gap-1 ml-auto">
                          <Users className="w-3 h-3" style={{ color: textSec }} />
                          <span className="text-xs" style={{ color: textSec }}>{policy.total_votes} recorded</span>
                        </div>
                      </div>
                      {/* Progress bar */}
                      <div className="h-2 border border-black overflow-hidden flex">
                        <div className="h-full" style={{ width: `${ayePct}%`, backgroundColor: "#10B981" }} />
                        <div className="h-full flex-1" style={{ backgroundColor: "#EF4444" }} />
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
